"use client"

import { useState, useMemo, useRef } from "react"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter"
import { oneLight } from "react-syntax-highlighter/dist/esm/styles/prism"
import { Copy, Check } from "lucide-react"
import { ChartBlock } from "./ChartBlock"

function countFences(text: string): number {
  return (text.match(/^```/gm) || []).length
}

function completePartialMarkdown(text: string): string {
  return countFences(text) % 2 === 0 ? text : text + "\n```"
}

// True when every fenced block in the raw content is closed. Used by chart
// blocks to distinguish in-flight streaming from a fully-received payload.
function isFenceClosed(text: string): boolean {
  return countFences(text) % 2 === 0
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)
  const handleCopy = () => {
    navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }
  return (
    <button
      onClick={handleCopy}
      className="rounded-sm p-1 text-muted-foreground transition-colors duration-150 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-coral/50"
      aria-label="Copy code"
    >
      {copied ? <Check size={14} /> : <Copy size={14} />}
    </button>
  )
}

export function MarkdownRenderer({ content }: { content: string }) {
  // The components object must stay reference-stable across renders: if it
  // rebuilds on every streaming token, react-markdown remounts the ChartBlock
  // subtree and Chart.js crashes on the detached canvas. Read the latest
  // fence-close state via ref so the deps array can stay empty.
  const completeRef = useRef(isFenceClosed(content))
  completeRef.current = isFenceClosed(content)
  // react-markdown v10 + React 19 has overly strict component types for element-specific refs.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const components: Record<string, any> = useMemo(
    () => ({
      code({ className, children }: { className?: string; children?: React.ReactNode }) {
        const match = /language-(\w+)/.exec(className || "")
        const codeString = String(children).replace(/\n$/, "")
        if (className === "language-chart") {
          return <ChartBlock complete={completeRef.current} jsonStr={codeString} />
        }
        if (match) {
          return (
            <div className="my-3 overflow-hidden rounded-lg border border-border bg-card shadow-e1">
              <div className="flex items-center justify-between border-b border-border bg-muted px-3 py-1">
                <span className="label-mono">{match[1]}</span>
                <CopyButton text={codeString} />
              </div>
              <SyntaxHighlighter
                style={oneLight}
                language={match[1]}
                PreTag="div"
                customStyle={{
                  margin: 0,
                  padding: "0.75rem",
                  fontSize: "0.8rem",
                  background: "transparent",
                }}
              >
                {codeString}
              </SyntaxHighlighter>
            </div>
          )
        }
        return (
          <code className="rounded-sm bg-muted px-1 py-0.5 font-mono text-[0.85em]">
            {children}
          </code>
        )
      },
      pre({ children }: { children?: React.ReactNode }) {
        return <>{children}</>
      },
      // Tables scroll inside their own container instead of stretching the
      // message column: wide result sets are common here, and without this
      // wrapper the html/body overflow clamp just crops them.
      table({ children }: { children?: React.ReactNode }) {
        return (
          <div className="my-3 overflow-x-auto rounded-lg border border-border shadow-e1">
            <table>{children}</table>
          </div>
        )
      },
    }),
    []
  )

  if (!content) return null
  return (
    // Prose blocks cap at max-w-prose (~65ch) for a comfortable line length;
    // tables, charts and code keep the full column. Table cells drop the full
    // cell grid for row separators: the scroll wrapper above owns the frame.
    <div className="markdown-body leading-relaxed [&_p]:my-2 [&_p]:max-w-prose [&_ul]:my-2 [&_ul]:pl-5 [&_ul]:list-disc [&_ol]:my-2 [&_ol]:pl-5 [&_ol]:list-decimal [&_li]:my-1 [&_li]:max-w-prose [&_h1]:text-lg [&_h1]:font-semibold [&_h1]:tracking-tight [&_h1]:mt-4 [&_h1]:mb-1.5 [&_h2]:text-base [&_h2]:font-semibold [&_h2]:tracking-tight [&_h2]:mt-3 [&_h2]:mb-1 [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:mt-2.5 [&_h3]:mb-1 [&_blockquote]:border-l-2 [&_blockquote]:border-brand-coral/40 [&_blockquote]:pl-3 [&_blockquote]:my-2 [&_blockquote]:text-muted-foreground [&_table]:my-0 [&_table]:min-w-full [&_table]:border-collapse [&_table]:text-[0.8125rem] [&_th]:px-3 [&_th]:py-1.5 [&_th]:bg-muted [&_th]:border-b [&_th]:border-border [&_th]:text-left [&_th]:font-semibold [&_td]:px-3 [&_td]:py-1.5 [&_td]:border-b [&_td]:border-border/60 [&_tr:last-child>td]:border-b-0 [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {completePartialMarkdown(content)}
      </ReactMarkdown>
    </div>
  )
}
